import { useEffect, useRef, useState } from "react";

const REDUCED = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Runs a figure up from zero when its tile first appears.
 *
 * The point is not decoration: a dashboard that paints its numbers instantly
 * gives no clue which of them just changed, and the roll draws the eye to the
 * tiles while the page settles. It only plays on the first value - a poll that
 * ticks a counter by three would otherwise replay the whole animation.
 */
export function useCountUp(target: number, duration = 750): number {
  const [value, setValue] = useState(() => (REDUCED() ? target : 0));
  const played = useRef(false);

  useEffect(() => {
    if (played.current || REDUCED()) {
      setValue(target);
      return;
    }
    played.current = true;
    const started = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      const t = Math.min(1, (now - started) / duration);
      setValue(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}

/** Renders a number that rolls up on first paint. `format` shapes the result. */
export function CountUp({ value, format }: { value: number; format: (n: number) => string }) {
  return <>{format(useCountUp(value))}</>;
}
